import { useState, useRef, useCallback, useId } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  ButtonGroup,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import CashdeskTicketPreview, { CashdeskBlock, TextAlign } from './CashdeskTicketPreview';

interface CashdeskCreationSectionProps {
  offerCount?: number;
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
}

function CashdeskCreationSection({ offerCount = 1, onAddOffer, onOfferUsed }: CashdeskCreationSectionProps) {
  const { sidebarColor } = useLayoutPreferences();
  const uid = useId();
  const nextId = useRef(0);
  const genId = () => `block_${uid}_${nextId.current++}`;

  const [blocks, setBlocks] = useState<CashdeskBlock[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [showOfferPanel, setShowOfferPanel] = useState(false);
  const [showTextPanel, setShowTextPanel] = useState(false);
  const [showPersFieldDialog, setShowPersFieldDialog] = useState(false);

  const offerCodes = Array.from({ length: Math.max(1, offerCount) }, (_, i) => `$NUM_COUPON_${i}$`);

  const updateBlock = useCallback((id: string, patch: Partial<CashdeskBlock>) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks(prev => prev.filter(b => b.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
  }, []);

  const addTextBlock = () => {
    const id = genId();
    const newBlock: CashdeskBlock = {
      id, type: 'text', content: 'Zone de texte',
      x: 10, y: 10, width: 200, height: 60,
      fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none',
      textAlign: 'left', fontSize: 14, rotation: 0,
      zIndex: blocks.length + 1,
    };
    setBlocks(prev => [...prev, newBlock]);
    setSelectedId(id);
  };

  const addBarcodeBlock = (anchor: string) => {
    const id = genId();
    const newBlock: CashdeskBlock = {
      id, type: 'barcode', content: anchor, anchor,
      x: 10, y: 10, width: 210, height: 75,
      fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none',
      textAlign: 'left', fontSize: 14, rotation: 0,
      zIndex: blocks.length + 1,
    };
    setBlocks(prev => [...prev, newBlock]);
    setSelectedId(id);
    setShowOfferPanel(false);
    const match = anchor.match(/\$NUM_COUPON_(\d+)\$/);
    if (match) {
      const idx = parseInt(match[1]);
      onOfferUsed?.(idx);
      if (idx >= offerCount - 1) onAddOffer?.();
    }
  };

  const sel = blocks.find(b => b.id === selectedId) ?? null;

  const toggleBold = () => sel && updateBlock(sel.id, { fontWeight: sel.fontWeight === 'bold' ? 'normal' : 'bold' });
  const toggleItalic = () => sel && updateBlock(sel.id, { fontStyle: sel.fontStyle === 'italic' ? 'normal' : 'italic' });
  const toggleUnderline = () => sel && updateBlock(sel.id, { textDecoration: sel.textDecoration === 'underline' ? 'none' : 'underline' });
  const setAlign = (align: TextAlign) => sel && updateBlock(sel.id, { textAlign: align });
  const increaseFontSize = () => sel && sel.type === 'text' && sel.fontSize < 82 && updateBlock(sel.id, { fontSize: sel.fontSize + 4 });
  const decreaseFontSize = () => sel && sel.type === 'text' && sel.fontSize > 10 && updateBlock(sel.id, { fontSize: sel.fontSize - 4 });

  const handleInsertPersonalField = (anchor: string) => {
    if (!sel || sel.type !== 'text') {
      const id = genId();
      setBlocks(prev => [...prev, {
        id, type: 'text', content: anchor,
        x: 10, y: 10, width: 200, height: 40,
        fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none',
        textAlign: 'left', fontSize: 14, rotation: 0,
        zIndex: blocks.length + 1,
      }]);
      setSelectedId(id);
    } else {
      updateBlock(sel.id, { content: sel.content + anchor });
    }
  };

  const toolBtnSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.82rem',
    backgroundColor: sidebarColor,
    color: '#fff',
    borderRadius: 1.5,
    '&:hover': { backgroundColor: sidebarColor, opacity: 0.88 },
  };

  const fmtBtnSx = (active: boolean) => ({
    color: active ? sidebarColor : '#495057',
    backgroundColor: active ? `${sidebarColor}18` : 'transparent',
  });

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <CashdeskTicketPreview
        blocks={blocks}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onUpdateBlock={updateBlock}
        onRemoveBlock={removeBlock}
      />

      <Box sx={{ flex: 1, minWidth: 280 }}>
        <Box
          sx={{
            border: '1px solid #e9ecef',
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, color: '#495057', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <span style={{ fontSize: '0.95rem' }}>🔧</span> Outils
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<QrCode2Icon fontSize="small" />}
              onClick={() => { setShowOfferPanel(v => !v); setShowTextPanel(false); }}
              sx={{ ...toolBtnSx, backgroundColor: showOfferPanel ? `${sidebarColor}dd` : sidebarColor }}
            >
              Code opération
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddAltIcon fontSize="small" />}
              onClick={() => setShowPersFieldDialog(true)}
              sx={toolBtnSx}
            >
              Champ de Personnalisation
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<TextFieldsIcon fontSize="small" />}
              onClick={() => { setShowTextPanel(v => !v); setShowOfferPanel(false); }}
              sx={{ ...toolBtnSx, backgroundColor: showTextPanel ? `${sidebarColor}dd` : sidebarColor }}
            >
              Zone de texte
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhotoLibraryIcon fontSize="small" />}
              disabled
              sx={{ ...toolBtnSx, opacity: 0.5 }}
            >
              Bibliothèque d&apos;image
            </Button>
          </Box>
        </Box>

        {showOfferPanel && (
          <Box
            sx={{
              border: `1px solid ${sidebarColor}40`,
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: `${sidebarColor}06`,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, color: sidebarColor, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <QrCode2Icon sx={{ fontSize: '1rem' }} /> Code opération
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {offerCodes.map(code => (
                <Button
                  key={code}
                  variant="outlined"
                  size="small"
                  onClick={() => addBarcodeBlock(code)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontFamily: 'monospace',
                    borderColor: sidebarColor,
                    color: sidebarColor,
                    borderRadius: 1.5,
                  }}
                >
                  {code}
                </Button>
              ))}
              <Button
                variant="outlined"
                size="small"
                onClick={() => addBarcodeBlock('$NUM_CARTE_BC$')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontFamily: 'monospace',
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  borderRadius: 1.5,
                }}
              >
                $NUM_CARTE_BC$
              </Button>
            </Box>
          </Box>
        )}

        {showTextPanel && (
          <Box
            sx={{
              border: `1px solid ${sidebarColor}40`,
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: `${sidebarColor}06`,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, color: sidebarColor, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TextFieldsIcon sx={{ fontSize: '1rem' }} /> Zone de texte
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<TextFieldsIcon fontSize="small" />}
              onClick={addTextBlock}
              sx={{ ...toolBtnSx, mb: 2 }}
            >
              Insérer une zone de texte
            </Button>

            {sel && sel.type === 'text' && (
              <>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Mise en forme — bloc sélectionné
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <ButtonGroup size="small" variant="outlined" sx={{ '& .MuiButton-root': { minWidth: 34, px: 0.5, py: 0.5, borderColor: '#dee2e6' } }}>
                    <Button onClick={toggleBold} sx={fmtBtnSx(sel.fontWeight === 'bold')} title="Gras">
                      <FormatBoldIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                    <Button onClick={toggleUnderline} sx={fmtBtnSx(sel.textDecoration === 'underline')} title="Souligné">
                      <FormatUnderlinedIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                    <Button onClick={toggleItalic} sx={fmtBtnSx(sel.fontStyle === 'italic')} title="Italique">
                      <FormatItalicIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup size="small" variant="outlined" sx={{ '& .MuiButton-root': { minWidth: 34, px: 0.5, py: 0.5, borderColor: '#dee2e6' } }}>
                    <Button onClick={() => setAlign('left')} sx={fmtBtnSx(sel.textAlign === 'left')}><FormatAlignLeftIcon sx={{ fontSize: '1rem' }} /></Button>
                    <Button onClick={() => setAlign('center')} sx={fmtBtnSx(sel.textAlign === 'center')}><FormatAlignCenterIcon sx={{ fontSize: '1rem' }} /></Button>
                    <Button onClick={() => setAlign('right')} sx={fmtBtnSx(sel.textAlign === 'right')}><FormatAlignRightIcon sx={{ fontSize: '1rem' }} /></Button>
                    <Button onClick={() => setAlign('justify')} sx={fmtBtnSx(sel.textAlign === 'justify')}><FormatAlignJustifyIcon sx={{ fontSize: '1rem' }} /></Button>
                  </ButtonGroup>

                  <ButtonGroup size="small" variant="outlined" sx={{ '& .MuiButton-root': { minWidth: 34, px: 0.5, py: 0.5, borderColor: '#dee2e6' } }}>
                    <Button onClick={increaseFontSize} title="Augmenter taille"><TextIncreaseIcon sx={{ fontSize: '1rem' }} /></Button>
                    <Button onClick={decreaseFontSize} title="Réduire taille"><TextDecreaseIcon sx={{ fontSize: '1rem' }} /></Button>
                  </ButtonGroup>
                </Box>
              </>
            )}
          </Box>
        )}

        {!sel && blocks.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            Cliquez sur un bloc dans le ticket pour le sélectionner et le modifier.
          </Typography>
        )}
        {blocks.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            Utilisez les outils ci-dessus pour ajouter des éléments dans le ticket.
          </Typography>
        )}
      </Box>

      <InsertPersonalFieldsDialog
        open={showPersFieldDialog}
        onClose={() => setShowPersFieldDialog(false)}
        onInsert={handleInsertPersonalField}
        channel="cashdesk"
      />
    </Box>
  );
}

export default CashdeskCreationSection;
